<template>
  <div class="unscheduled-treatment-pg">
    <!-- Follow-ups redesign (docs/plans/2026-10-02-followups-redesign-plan.md, Phase 3). Super
         Admins get the new branch unless they switch; offices follow STAGE.overdueRecallList (src/utils/followups/uiVersion.js);
         the v-else branch below is the classic page, unchanged. The page still owns its data, the
         list and its remove / delete methods — the new branch only binds them to the shared components. -->
    <div v-if="fuUseNewUi" class="fu-list" data-test="fu-overdue-recall-list">
      <followups-ui-toggle target="old" page-key="overdueRecallList" @toggle="fuSetUi" />
      <followups-page-shell
        :title="getList ? getList.Name : 'Overdue recall list'"
        :lede="getList && getList.Description ? getList.Description : ''"
        :crumbs="fuCrumbs"
      >
        <template #meta>
          <p v-if="fuListMeta" class="fu-list__meta" data-test="fu-list-meta">{{ fuListMeta }}</p>
          <p v-if="fuListFilters.length" class="fu-list__filters" data-test="fu-list-filters">
            <span class="fu-list__filters-label">Made with</span>
            <span v-for="f in fuListFilters" :key="f.key" class="fu-list__filter">{{ f.label }}: <strong>{{ f.value }}</strong></span>
          </p>
        </template>
        <template #actions>
          <followups-filter-button
            label="Your lists"
            icon="list"
            data-test="fu-lists-menu"
            :popover="fuListsPopover"
            @select="fuLoadList"
          />
          <followups-filter-button
            label="Delete list"
            icon="trash"
            :chevron="false"
            data-test="fu-delete-list"
            :disabled="!dataToDisplay"
            @click="fuDialog = 'delete'"
          />
        </template>

        <ErrorDisplay/>
        <followups-list-summary queue="overdueRecall" :rows="fuRows" :busy="overlayshow" />
        <followups-queue
          queue="overdueRecall"
          mode="list"
          :list-id="listID"
          :rows="fuRows"
          :selected="selectedPatients"
          :busy="overlayshow"
          @toggle-row="row => updateSelectedPatients(selectedPatients, row)"
          @toggle-all="fuToggleAll"
          @remove-patient-from-list="fuAskRemove"
          @clear-selection="clearSelection"
          @add-to-existing-list="addToExistingList"
          @create-new-list="createNewList"
        />
      </followups-page-shell>

      <!-- Remove: ref'd like the old b-modal, so removePatient()'s hideModal() closes this one. -->
      <followups-list-dialog
        v-if="fuDialog === 'remove'"
        ref="remove-patient-from-list"
        kind="remove"
        :title="fuRemoveTitle"
        description="They’ll still show up in Overdue recall with their follow-up history intact."
        @confirm="removePatient"
        @close="fuDialog = null"
      />
      <followups-list-dialog
        v-if="fuDialog === 'delete'"
        kind="delete"
        :title="fuDeleteTitle"
        :description="fuDeleteDescription"
        @confirm="fuDeleteList"
        @close="fuDialog = null"
      />
    </div>
    <template v-else>
    <followups-ui-toggle target="new" page-key="overdueRecallList" @toggle="fuSetUi" />
    <h2 class="page-title">
      Overdue Recall > <small v-if="getList">{{this.getList.Name}}</small>
      <b-button
          variant="danger"
          size="sm"
          class="float-right mr-3"
          @click="showSureModal()"
        >Delete List</b-button>
    </h2>
    <b-modal id="sure" ref="sure-modal" size="sm" body-class="bg-white" title="Are you sure?" @ok="removeList" @cancel="hideSureModal">
            Are you sure you want to delete this list?
    </b-modal>
    <b-row>
      <!-- Follow-up Attempts Chart Panel -->
      <b-col xs="12" md='6' lg="4" class="mb-3">
        <div class="modern-filter-panel h-100">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-pie-chart section-icon"></i>
              Follow-up Attempts
            </h5>
          </div>
          <div class="filter-content d-flex align-items-center justify-content-center">
            <div id="attempts-chart" class="chart-container">
              <donut-chart
                v-if="dataToDisplay"
                id="attempts-chart"
                :data="getAttemptsData"
                colors='[ "#3abf94", "#60a5fa", "#f59e0b" ]'
                resize="true"
                class="text-center"
                >
              </donut-chart>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Outcome Chart Panel -->
      <b-col xs="12" md='6' lg="4" class="mb-3">
        <div class="modern-filter-panel h-100">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-pie-chart section-icon"></i>
              Outcome
            </h5>
          </div>
          <div class="filter-content d-flex align-items-center justify-content-center">
            <div id="outcome-chart" class="chart-container">
              <donut-chart
                v-if="dataToDisplay"
                id="outcome-chart"
                :data="getOutcomeData"
                colors='[ "#10b981", "#3b82f6", "#ef4444" ]'
                resize="true"
                class="text-center"
                >
              </donut-chart>
            </div>
          </div>
        </div>
      </b-col>

      <!-- List Info Panel -->
      <b-col xs="12" md='12' lg="4" class="mb-3">
        <div class="modern-filter-panel h-100" v-if="getList">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-th-list section-icon"></i>
              Your Lists
            </h5>
          </div>
          <div class="filter-content">
            <div class="filter-group">
              <label class="filter-label">Overdue Recall Lists</label>
              <b-dropdown id="simple-select" :text="this.getList.Name" variant="default" class="w-100" boundary="window">
                <b-dropdown-item-button v-for="list in this.getLists" :key="list.ListID" @click="loadList(list.ListID, list.Name)">
                 <i class="glyphicon glyphicon-th-list text-secondary mr-xs mb-xs" /> {{list.Name}}
                </b-dropdown-item-button>
              </b-dropdown>
            </div>

            <p class="mt-3 fs-13"><span class="fw-semi-bold">Description: </span>
              <span class="mx-1">
                {{this.getList.Description}}
              </span>
            </p>

            <p class="mt-2 fs-13" ><span class="fw-semi-bold">Created by:</span>
              <span class="mx-1">
                {{this.getList.EmployeeName}}
              </span>
              on <span>{{this.getList.CreatedAt | dateformat(dateFormatStr)}}</span>
            </p>

            <div class="filters-wrap" v-if="this.filtersApplied">
              <h5 class="text-success mr-2 fs-13">Filters:</h5>

              <div class="d-flex flex-wrap align-items-baseline flex-grow-1">
                <h6 class="fw-semi-bold mr-2 fs-13" v-if="this.getList.FilterDates">Tx Plan Date - <span class="font-weight-normal"> {{this.getList.FilterDates}}</span></h6>
                <span class="fw-semi-bold mr-2 fs-13 vertical-line">|</span>
                <h6 class="fw-semi-bold mr-2 fs-13" v-if="this.getList.FilterProvider">Providers -<span class="font-weight-normal"> {{this.getList.FilterProvider === '' ? 'All Proviers' : this.getList.FilterProvider}}</span></h6>
                <span class="fw-semi-bold mr-2 fs-13 vertical-line">|</span>
                <h6 class="fw-semi-bold mr-2 fs-13" v-if="this.getList.FilterCarriers">Carrier(s) - <span class="font-weight-normal">{{this.getList.FilterCarriers}}</span></h6>
                <span class="fw-semi-bold mr-2 fs-13 vertical-line">|</span>
                <h6 class="fw-semi-bold mr-2 fs-13" v-if="this.getList.FilterCodes">Include Codes - <span class="font-weight-normal">{{this.getList.FilterCodes}}</span></h6>
                <span class="fw-semi-bold mr-2 fs-13 vertical-line">|</span>
                <h6 class="fw-semi-bold mr-2 fs-13" v-if="this.getList.FilterAmountRemainingOver">Amount remaining over - <span class="font-weight-normal"><span v-if="this.getList.FilterAmountRemainingOver">$</span>{{this.getList.FilterAmountRemainingOver}}</span></h6>
              </div>
            </div> <!-- filters-wrap  -->

            <h5 class="fw-semi-bold mb-0 fs-13" v-if="dataToDisplay">Patient count: <span class="fw-bold ml-1" style="color: #444;">{{this.dataToDisplay[0].length}}</span></h5>

            <hr/>
            <p class="mb-0 fs-13">
              To view all patients of Overdue Recall, go to
              <router-link :to="{ name: 'OverdueRecall' }" class="text-underline">Default View</router-link>
            </p>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col>
        <div class="modern-table-panel">
          <div class="panel-header">
            <h5 class="section-title" v-if="getList">
              <i class="fa fa-list section-icon"></i>
              Overdue Recall List: <span style="color: #444;">{{this.getList.Name}}</span>
            </h5>
            <p class="panel-subtitle">Patients with overdue recall appointments that need follow-up.</p>
          </div>
          <div class="table-content">
            <CommonOverlay
              :show="overlayshow"
              title="Loading Overdue Recall List"
              message="Please wait while we load the recall list data."
            />

            <AdminTable
              v-if="dataToDisplay"
              :data="dataToDisplay[0]"
              :columns="columns"
              :options="options"
              :extra="teamioTableExtraParams"
              :selectedPatients="selectedPatients"
              :showBulkActions="true"
              :tableTitle="'Overdue Recall List: ' + (getList ? getList.Name : '')"
              @remove-patient-from-list="showModal"
              @clear-selection="clearSelection"
              @add-to-existing-list="addToExistingList"
              @create-new-list="createNewList"
            />
          </div>
        </div>
      </b-col>
    </b-row>
    <b-modal ref="remove-patient-from-list" id="remove-patient-from-list" title="Delete" body-class="bg-white">
      <p>Are you sure you want to remove this patient from this list?</p>
      <div slot="modal-footer" class="w-100">
          <b-button variant="danger" size="sm" class="float-right" @click="hideModal('remove-patient-from-list')">No</b-button>
          <b-button variant="success" size="sm" class="float-right mr-3" @click="removePatient">Yes</b-button>
      </div>
    </b-modal>

    </template>
    <!-- The new branch's ONE slide-out outlet (the follow-up drawer). The legacy branch's outlet
         lives inside AdminTable; v-if / v-else keeps the two from ever being mounted together. -->
    <slideout-panel v-if="fuUseNewUi"></slideout-panel>
  </div> <!-- overdue-recall-pg -->
</template>

<script>

/* eslint-disable */  // TODO: Remove this from everwhere

import Raphael from 'raphael/raphael'
global.Raphael = Raphael
import Vue from "vue";
import { DonutChart } from 'vue-morris'
import { mapState, mapActions, mapMutations } from 'vuex';
import $ from 'jquery';
import 'imports-loader?jQuery=jquery,this=>window!webpack-raphael/raphael';
import 'imports-loader?jQuery=jquery,this=>window!govpredict-morris/morris';
import Widget from '@/components/Widget/Widget';
import _ from 'lodash';
import moment from 'moment-timezone';
import mixin from '../../mixin.js';
import AdminTable from '@/pages/Admin/AdminTable';
import CommonOverlay from '@/components/CommonOverlay.vue';
// Follow-ups redesign (Phase 3) — the new branch of the template. See fuUseNewUi.
import ErrorDisplay from '@/components/ErrorDisplay/ErrorDisplay';
import FollowupsQueue from '@/components/Followups/FollowupsQueue.vue';
import FollowupsPageShell from '@/components/Followups/FollowupsPageShell.vue';
import FollowupsListSummary from '@/components/Followups/FollowupsListSummary.vue';
import FollowupsFilterButton from '@/components/Followups/FollowupsFilterButton.vue';
import FollowupsListDialog from '@/components/Followups/FollowupsListDialog.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';
import { switchUi } from '@/utils/followups/uiSwitch.js';
import { sortListsNewestFirst } from '@/utils/followups/pageChrome.js';
import { listFilters } from '@/utils/followups/listSummary.js';

const { Morris } = window;

export default {
  name:'OverdueRecallList',
  props: ['listID'],
  components:{Widget, DonutChart, AdminTable, CommonOverlay,
    ErrorDisplay, FollowupsQueue, FollowupsPageShell, FollowupsListSummary, FollowupsFilterButton, FollowupsListDialog, FollowupsUiToggle},
  mixins: [mixin],
  
  created() {
    this.init();
  },

  data() {
    return {
      overlayshow: false,
      selectedTask: null,
      results: [],
      selectedPatients: [], // For mass messaging functionality
      teamioTableExtraParams: {
        detailPageName: 'OverdueRecallDetails',
        listID: this.listID
      },
      recallStatusColors: {
        'Archived': 'grey',
        'Call Back, Not Ready': 'primary',
        'Couldn\'t Leave Voicemail': 'pink',
        'Left Voicemail' : 'f87979',
        'No Answer': 'purple',
        'Not Called': 'orange',
        'Patient Asked Not To Be Called': 'danger',
        'Scheduled': 'success',
        'Patient Will Call Us': 'FF6384',
      },
      chartOptions: {
        legend: {
          display: true,
          position: 'right'
        },
        responsive: true,
        maintainAspectRatio: false
      },

      //columns: ['Patient', 'Age', 'DateDue', 'RecallType', 'RecallInterval', 'Provider', 'Carrier', 'Status','FollowUpActionPlan', 'Attempts', 'Actions'],
      options: {
          headings: {
            AutoRemindersSent: 'Auto Reminders Sent'
          },          
          skin:'table',
          sortable: ['Patient', 'Age', 'DateDue', 'RecallType', 'RecallInterval', 'Provider', 'Carrier', 'Status', 'Attempts'],
          perPage: 50,
          pagination: { chunk: 10, dropdown: false },
          sortByColumn: true,
          filterByColumn: true,
          filterable: ['Patient', 'Carrier', 'AutoRemindersSent', 'LastHygienist', 'Status', 'Attempts'],
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          },
          sortIcon: {
            base: "fa",
            is: "fa-sort",
            up: "fa-sort-asc",  
            down: "fa-sort-desc"
          },
          // columnsClasses: {id:'padding-top-0'}
      },
      // Follow-ups redesign (new branch only) — UI state; the rows, the list and selectedPatients
      // stay the truth.
      fuUiVersion: 0,      // bumped by the Classic / New view links so fuUseNewUi re-reads storage
      fuDialog: null,      // 'remove' | 'delete' | null
      fuRemoving: null,    // the row the remove dialog names (selectedTask is what removePatient reads)
      filtersApplied : false
    }
  },

  methods:{
    ...mapActions('alert', ['setDismissSuccessCountDown']),
    ...mapActions('teamio', ['runQuery']),
    ...mapActions('lists',['deleteList', 'removePatientFromList']),
    ...mapMutations('teamio', ['emptyResults']),

    // ---- Follow-ups redesign (new branch only). Each one hands off to the page's EXISTING
    // ---- methods (loadList, removePatient, removeList, the selection helpers); none re-implements them.

    /** The "Classic view" / "New view" links: a Super Admin re-renders in place, an office user reloads (uiSwitch.js). */
    fuSetUi(target) {
      if (switchUi('overdueRecallList', target, { isSuperAdmin: this.isSuperAdmin, userKey: this.userID }) === 'rerender') this.fuUiVersion += 1;
    },

    fuLoadList(list) {
      this.loadList(list.id, list.name);
    },

    fuToggleAll({ rows, checked }) {
      if (checked) this.chooseAll(rows, this.selectedPatients);
      else this.chooseNone(this.selectedPatients);
    },

    // The table's trash icon. The old modal's showModal() did the same first step (remember the
    // row in selectedTask); the dialog then takes the b-modal's place and its Remove runs the
    // existing removePatient(), which closes it through hideModal('remove-patient-from-list').
    fuAskRemove(row) {
      this.selectedTask = row;
      this.fuRemoving = row;
      this.fuDialog = 'remove';
    },

    // The old "Are you sure?" b-modal's OK closed the modal and ran removeList(); so does this.
    fuDeleteList() {
      this.fuDialog = null;
      return this.removeList();
    },

    async init() {
        this.overlayshow = true;
        let query = {
          type: 'OVERDUE_RECALL_LIST',
          siteID: this.siteID,
          listID: this.listID,
          dateFrom: null,
          dateTo: null,
          provider: null,
          insPlans: null,
          _date: moment().format('YYYY-MM-DD'),
          country: localStorage.getItem('country') || 'USA'
        };

        try {
          this.emptyResults();
          this.results = await this.runQuery(query);
          this.overlayshow = false;
        } catch (error) {
          this.overlayshow = false;
          this.$toast.error("Error loading recall list data. Please try again.");
          console.error(error);
        }
    },

    isDueToday(d) {
      if(d === null || d === '')
        return false;
      else
        return moment(d).isSame(moment(), 'day');
    },

    loadList(listID, name) {
      this.$router.push({
        name: 'OverdueRecallList',
        params: {
          listID: listID,
        }
      });
    },

    changeSelectOption(field, value) {
      Vue.set(this, field, value);
    },

    successCountDownChanged(count) {
        this.setDismissSuccessCountDown(count);
    },

    showSureModal() {
      this.$refs['sure-modal'].show();
    },

    hideSureModal() {
      this.$refs['sure-modal'].hide();
    },

    showModal(e) {
      //console.log(`in showModal, e: ${JSON.stringify(e)}`);
      //console.log(`in showModal, name: ${name} and task: ${JSON.stringify(task)}`);
      this.selectedTask = e;
      this.$refs['remove-patient-from-list'].show();
    },

    hideModal(ref) {
      this.$refs[ref].hide()
    },

    async removePatient() {
      let payload = {
        listID: this.listID,
        siteID: this.siteID,
        patNum: this.selectedTask.PatNum
      };
      //console.log(`remove patient from list payload: ${JSON.stringify(payload)}`);

      try {
        this.hideModal('remove-patient-from-list');
        this.overlayshow = true;
        await this.removePatientFromList(payload);
        this.$toast.success("Patient removed successfully!");
        if(!this.errored) {
          this.overlayshow = false;
          this.$router.go();
        }
      } catch (err) {
        this.overlayshow = false;
        this.$toast.error("Error removing patient. Please try again later");
        console.error(err);
      }
    },

    async removeList(){
      if(this.dataToDisplay) {
        this.overlayshow = true;
        let payload;
        payload = {
          ListID: this.listID,
          TaskTypeID: 3, // OVERDUE RECALL
          SiteID: this.siteID,
          ListPatients: this.dataToDisplay[0]
        };

        try {
          await this.deleteList(payload);
          this.overlayshow = false;
          if(!this.errored) {
            this.$router.push({name: 'OverdueRecall'});
          }
        } catch (err) {
          this.overlayshow = false;
          this.$toast.error("Error deleting list. Please try again later");
          console.error(err);
        }
      }
    },


    clearSelection() {
      this.selectedPatients = [];
    },

    // List management methods (called by AdminTable)
    addToExistingList(selectedPatients) {
      // For list pages, we might want to show a modal to select which list to add to
      // For now, show a simple message
      this.$toast.info('Add to Existing List functionality - to be implemented for list pages');
    },

    createNewList(selectedPatients) {
      // For list pages, we might want to show a modal to create a new list
      // For now, show a simple message  
      this.$toast.info('Create New List functionality - to be implemented for list pages');
    },

    // Patient selection methods (used by AdminTable)
    updateSelectedPatients(selectedPatients, patient) {
      const index = selectedPatients.findIndex(p => p.PatNum === patient.PatNum);
      if (index > -1) {
        selectedPatients.splice(index, 1);
      } else {
        selectedPatients.push(patient);
      }
    },

    isSelectedPatient(selectedPatients, patNum) {
      return selectedPatients.some(p => p.PatNum === patNum);
    },

    chooseAll(data, selectedPatients) {
      // Clear existing selection first
      selectedPatients.splice(0, selectedPatients.length);
      // Add all patients
      data.forEach(patient => {
        selectedPatients.push(patient);
      });
    },

    chooseNone(selectedPatients) {
      selectedPatients.splice(0, selectedPatients.length);
    }
  },

  computed:{
    ...mapState({
        _lists: state => state.lists.lists,
    }),

    // ---- Follow-ups redesign (new branch only) -------------------------------------------------

    /**
     * Which template branch renders. An office user → STAGE.overdueRecallList and their own choice (the old page at "preview"). A Super Admin → the new page unless "Classic view" was picked.
     */
    fuUseNewUi() {
      // eslint-disable-next-line no-unused-expressions
      this.fuUiVersion; // re-read storage after the Classic / New view link
      return shouldUseNewFollowupsUi('overdueRecallList', { isSuperAdmin: this.isSuperAdmin, userKey: this.userID, storage: browserStorage() });
    },

    // The rows AdminTable gets (dataToDisplay[0]); [] while loading.
    fuRows() {
      return this.dataToDisplay && this.dataToDisplay[0] ? this.dataToDisplay[0] : [];
    },

    // Back to the queue — the old List Info panel's "go to Default View" link.
    fuCrumbs() {
      return [{ label: 'Follow-ups' }, { label: 'Overdue recall', to: { name: 'OverdueRecall' } }, { label: 'List' }];
    },

    // The old List Info panel's "Created by … on …" and "Patient count" lines, as one line.
    fuListMeta() {
      const l = this.getList;
      if (!l) return '';
      const fmt = this.dateFormatStr || 'MM/DD/YYYY';
      const created = ['Created', l.EmployeeName ? `by ${l.EmployeeName}` : '', l.CreatedAt ? `on ${moment(l.CreatedAt).format(fmt)}` : '']
        .filter(Boolean).join(' ');
      const n = this.fuRows.length;
      return this.dataToDisplay ? `${created} · ${n} patient${n === 1 ? '' : 's'}` : created;
    },

    // The filters the list was made with (the old panel's Filters block, which never showed).
    fuListFilters() {
      return listFilters(this.getList, { datesLabel: 'Recall due' });
    },

    fuListsPopover() {
      const fmt = this.dateFormatStr || 'MM/DD/YYYY';
      return {
        kind: 'lists',
        title: 'Overdue recall lists',
        width: 340,
        estHeight: 380,
        align: 'right',
        emptyText: 'No lists yet.',
        listsFoot: 'list',
        lists: sortListsNewestFirst(this.getLists || []).map(l => ({
          id: l.ListID,
          name: l.Name,
          meta: [l.CreatedAt ? `Created ${moment(l.CreatedAt).format(fmt)}` : '', l.EmployeeName || ''].filter(Boolean).join(' · '),
          current: l.ListID === this.listID,
        })),
      };
    },

    fuRemoveTitle() {
      const name = this.fuRemoving && this.fuRemoving.Patient;
      return name ? `Remove ${name} from this list?` : 'Remove from this list?';
    },

    fuDeleteTitle() {
      return this.getList && this.getList.Name ? `Delete “${this.getList.Name}”?` : 'Delete this list?';
    },

    fuDeleteDescription() {
      const n = this.fuRows.length;
      return `${n === 1 ? 'Its 1 patient stays' : `Its ${n} patients stay`} in Overdue recall — they just won’t be grouped anymore. Follow-up history isn’t affected.`;
    },

    dataToDisplay() {
      if(this.results && this.results.data && this.results.data.length > 0)
          return this.results.data;
    },

    isAptReminderPaidFor() {
      if(this.allSettings && this.allSettings.APPT_REMINDER_PAID_FOR) {
        if (this.allSettings.APPT_REMINDER_PAID_FOR.PropertyValue === 'TRUE')
          return true;
        else
          return false;
      } else
        return false;
    },

    columns() {
      if(this.isAptReminderPaidFor)
        return ['id', 'Patient', 'LastHygienist', 'DateDue', 'RecallType', 'RecallInterval', 'Provider', 'Carrier', 'AutoRemindersSent', 'Status','FollowUpActionPlan', 'Attempts', 'Actions'];        
      else
        return ['id', 'Patient', 'LastHygienist', 'DateDue', 'RecallType', 'RecallInterval', 'Provider', 'Carrier', 'LastHygienist', 'Status','FollowUpActionPlan', 'Attempts', 'Actions'];
    },

    getAttemptsData() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0) {
        let x, times;
        let retVal = [];

        let temp = this.dataToDisplay[0];
        // replace any null with 0 so that the chart doesn't say "null times"
        temp = _.map(temp, function(a) {
          if (a.Attempts === null)
            a.Attempts = 0;
          return a;
        });

        if(temp) {
          let attempts = _.map(_.uniqBy(temp, 'Attempts'), 'Attempts');
          //console.log(JSON.stringify(attempts));
          attempts.forEach(function(attempt) {
            x = temp.filter(task => task.Attempts === attempt).length;
            //console.log('x:' + x);
            times = attempt <= 1 ? 'time' : 'times';
            retVal.push({
              label: 'Pts contacted ' + attempt + ' ' + times,
              value: x
            });
          });

          //console.log('retval:' + JSON.stringify(retVal));
          return retVal;
        }
      }
    },

    getOutcomeData() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0) {
        let x;
        let retVal = [];

        let temp = this.dataToDisplay[0];
        if(temp) {
          let statuses = _.map(_.uniqBy(temp, 'Status'), 'Status');
          //console.log(JSON.stringify(statuses));
          statuses.forEach(function(status) {
            x = temp.filter(task => task.Status === status).length;
            //console.log('x:' + x);
            retVal.push({
              label: status,
              value: x
            });
          });
          
          //console.log('retval:' + JSON.stringify(retVal));
          return retVal;
        }
      }
    },
  
     chartStyles() {
      return {
        width: '100%',
        height: '180px',
      }
    },

    getLists() {
      /*
      Comment by Nilay: initially we were filtering by whoever this list was assigned to. But we dont want that,
      All lists should be able to be worked on by anyone as long as they're assigned as accountable person for Overdue recall.
      As of now, our list creation process simply groups them into "buckets" like "tina's patients", "dr.k's patients", etc.
      Anyone can work on them, as long as they're assigned to do that type of task!

      */
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[1];
        //return this.dataToDisplay[1].filter(l => l.AssignedToEmpID === this.userID);
    },

    getList() {
      if(this.dataToDisplay && this.dataToDisplay[1] && this.dataToDisplay[1].length > 0) {
        let lists = this.dataToDisplay[1].filter(list => list.ListID === this.listID );
        if(lists.length > 0)
          return lists[0];
      }
    },


    isMainChecked() {
      if (!this.dataToDisplay || !this.dataToDisplay[0] || this.dataToDisplay[0].length === 0) return false;
      return this.selectedPatients.length === this.dataToDisplay[0].length;
    }

  },
  
}
</script>

<!-- The redesign's new branch (.fu-list) — the shared list-page styles live with the Follow-ups pages. -->
<style src="../Followups/list-page.scss" lang="scss" scoped />
<style src="./OverdueRecallList.scss" lang="scss" scoped />